import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import AxisPropertiesFields from "./AxisPropertiesFields";
import { useLidarClearance } from "./LidarClearanceContext";
import {
  type AxisProperties,
  isValidAxisProperties,
  updateAxis,
} from "./api";
import type { LidarClearanceAxis } from "./schemas";
import t from "./translations";

type Props = {
  axis: LidarClearanceAxis | null;
  onClose: () => void;
};

function toProperties({
  region,
  id,
  ...properties
}: LidarClearanceAxis): AxisProperties {
  return properties;
}

export default function AxisDetailsDrawer({ axis, onClose }: Props) {
  const { refetch } = useLidarClearance();
  const [properties, setProperties] = useState<AxisProperties | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setProperties(axis ? toProperties(axis) : null);
  }, [axis]);

  const original = useMemo(() => (axis ? toProperties(axis) : null), [axis]);

  if (!axis || !properties || !original) return null;

  const dirty = (Object.keys(original) as (keyof AxisProperties)[]).some(
    (key) => properties[key] !== original[key]
  );
  const canSave = dirty && !saving && isValidAxisProperties(properties);

  const save = async () => {
    setSaving(true);
    try {
      await updateAxis(axis.region, axis.id, {
        ...properties,
        name_en: properties.name_en.trim(),
      });
      await refetch();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.updateAxis" });
      toast.error(t.FailedToUpdateAxis);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={axis !== null} onClose={onClose}>
      <Box sx={{ width: 420, p: 3 }} data-testid="axis-details-drawer">
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <TextField
              label={t.NameEnLabel}
              helperText={t.NameVisibleToCustomersNote}
              value={properties.name_en}
              onChange={(event) =>
                setProperties({ ...properties, name_en: event.target.value })
              }
              sx={{ flexGrow: 1 }}
              slotProps={{ htmlInput: { "data-testid": "axis-drawer.name" } }}
            />
            <Tooltip title={t.SaveButton}>
              <span>
                <IconButton
                  color="primary"
                  disabled={!canSave}
                  onClick={save}
                  data-testid="axis-drawer.save"
                  sx={{ mt: 1 }}
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <AxisPropertiesFields
            region={axis.region}
            value={properties}
            onChange={setProperties}
            disabled={saving}
          />
        </Stack>
      </Box>
    </Drawer>
  );
}
